import React, { useEffect, useMemo, useRef } from "react";
import { observer } from "mobx-react";
import { DesignShowcaseViewStore } from "../../store";
import { DesignShowcaseView } from "../view/DesignShowcaseView";
import { DesignShowcaseUpdateStore } from "../../store";
import { DesignShowcaseUpdate } from "../update/DesignShowcaseUpdate";
import { useRootStore } from "../../../../store/contexts/RootStoreContext";
import { Box, CircularProgress } from "@mui/material";

export type DesignShowcaseModalContentProps = {
  entityId: string;
  mode: "view" | "update";
  onClose: () => void;
};

export const DesignShowcaseModalContent: React.FC<DesignShowcaseModalContentProps> =
  observer(({ entityId, mode, onClose }) => {
    const rootStore = useRootStore();
    const initializedRef = useRef<string | null>(null);

    const storeInfo = useMemo(() => {
      if (mode === "update") {
        const s = new DesignShowcaseUpdateStore(rootStore);
        s.isModalMode = true;
        return { store: s, type: "update" as const };
      }
      // Default to view mode
      const s = new DesignShowcaseViewStore(rootStore);
      s.isModalMode = true;
      return { store: s, type: "view" as const };
    }, [rootStore, mode]);

    useEffect(() => {
      if (entityId && initializedRef.current !== entityId) {
        initializedRef.current = entityId;
        storeInfo.store.initialize({ id: entityId });
      }
    }, [entityId, storeInfo]);

    useEffect(() => {
      return () => {
        storeInfo.store.destroy();
      };
    }, [storeInfo]);

    const store = storeInfo.store;
    if (store.status === "processing" || store.status === "uninitialized") {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 200,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Box sx={{ p: 2 }}>
        {storeInfo.type === "view" && (
          <DesignShowcaseView
            designShowcaseViewStore={storeInfo.store as DesignShowcaseViewStore}
          />
        )}
        {storeInfo.type === "update" && (
          <DesignShowcaseUpdate
            designShowcaseUpdateStore={
              storeInfo.store as DesignShowcaseUpdateStore
            }
            isModalMode
            onModalClose={onClose}
          />
        )}
      </Box>
    );
  });
